import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Box, Card, Grid, IconButton, MenuItem, Select, TextField, Typography,} from "@mui/material";
import {addMonths, format, subMonths} from "date-fns";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import DynamicTable from "./DynamicTable";
import MDBox from "components/MDBox";
import DashboardLayout from "core/LayoutContainers/DashboardLayout";
import {Bar, Doughnut} from "react-chartjs-2";
import {ArcElement, BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip,} from "chart.js";
import BudgetService from "services/budget-service";

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const months = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const BudgetDashboard = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isFlipping, setIsFlipping] = useState(false);
  const [budgetPeriod, setBudgetPeriod] = useState({
    start: null,
    end: null,
  });
  const [currency, setCurrency] = useState("$");
  const [budgetId, setBudgetId] = useState(null);
  const [loading, setLoading] = useState(true);

  // Currency mapping
  const currencySymbolToCode = {
    "$": "USD",
    "€": "EUR",
    "£": "GBP",
  };

  const currencyCodeToSymbol = {
    "USD": "$",
    "EUR": "€",
    "GBP": "£",
  };

  // Financial Overview data
  const [financialOverview, setFinancialOverview] = useState({
    rollover: { planned: 0, actual: 0 },
    income: { planned: 0, actual: 0 },
    expenses: { planned: 0, actual: 0 },
    bills: { planned: 0, actual: 0 },
    savings: { planned: 0, actual: 0 },
    investing: { planned: 0, actual: 0 },
    debt: { planned: 0, actual: 0 },
  });

  // Income data
  const [incomeData, setIncomeData] = useState([]);

  // Expenses data
  const [expensesData, setExpensesData] = useState([]);

  // Bills data
  const [billsData, setBillsData] = useState([]);

  // Savings data
  const [savingsData, setSavingsData] = useState([]);

  // Debt data
  const [debtData, setDebtData] = useState([]);

  // Investing data
  const [investingData, setInvestingData] = useState([]);

  const incomeColumns = [
    { key: "name", label: "Name", type: "text" },
    { key: "amount", label: "Amount", type: "number" },
  ];

  const expensesColumns = [
    { key: "name", label: "Name", type: "text" },
    { key: "amount", label: "Amount", type: "number" },
  ];

  const billsColumns = [
    { key: "name", label: "Name", type: "text" },
    { key: "amount", label: "Amount", type: "number" },
  ];

  const savingsColumns = [
    { key: "name", label: "Name", type: "text" },
    { key: "amount", label: "Amount", type: "number" },
  ];

  const debtColumns = [
    { key: "name", label: "Name", type: "text" },
    { key: "amount", label: "Amount", type: "number" },
  ];

  const investingColumns = [
    { key: "name", label: "Name", type: "text" },
    { key: "amount", label: "Amount", type: "number" },
  ];

  const handleFinancialOverviewChange = useCallback((item, field, value) => {
    // Remove leading zeros and validate
    let cleanedValue = value;
    if (value && value !== '0' && value !== '0.') {
      cleanedValue = value.replace(/^0+(?=\d)/, '');
    }
    
    setFinancialOverview(prev => {
      const updated = {
        ...prev,
        [item]: {
          ...prev[item],
          [field]: cleanedValue
        }
      };
      
      // Save to backend when user updates planned values
      if (field === 'planned' && budgetId && cleanedValue && parseFloat(cleanedValue) >= 0) {
        const overview = {
          rollover: parseFloat(item === 'rollover' ? cleanedValue : prev.rollover.planned) || 0,
          actualRollover: parseFloat(prev.rollover.actual) || 0,
          income: parseFloat(item === 'income' ? cleanedValue : prev.income.planned) || 0,
          expenses: parseFloat(item === 'expenses' ? cleanedValue : prev.expenses.planned) || 0,
          bills: parseFloat(item === 'bills' ? cleanedValue : prev.bills.planned) || 0,
          savings: parseFloat(item === 'savings' ? cleanedValue : prev.savings.planned) || 0,
          investing: parseFloat(item === 'investing' ? cleanedValue : prev.investing.planned) || 0,
          debts: parseFloat(item === 'debt' ? cleanedValue : prev.debt.planned) || 0,
        };
        
        // Debounce or immediate update
        BudgetService.updateBudgetOverview(budgetId, overview)
          .catch(error => console.error('Error updating budget overview:', error));
      }
      
      // Also save when user updates actual rollover
      if (field === 'actual' && item === 'rollover' && budgetId && cleanedValue && parseFloat(cleanedValue) >= 0) {
        const overview = {
          rollover: parseFloat(prev.rollover.planned) || 0,
          actualRollover: parseFloat(cleanedValue) || 0,
          income: parseFloat(prev.income.planned) || 0,
          expenses: parseFloat(prev.expenses.planned) || 0,
          bills: parseFloat(prev.bills.planned) || 0,
          savings: parseFloat(prev.savings.planned) || 0,
          investing: parseFloat(prev.investing.planned) || 0,
          debts: parseFloat(prev.debt.planned) || 0,
        };
        
        BudgetService.updateBudgetOverview(budgetId, overview)
          .catch(error => console.error('Error updating budget overview:', error));
      }
      
      return updated;
    });
  }, [budgetId]);

  const handlePlannedBlur = useCallback((item) => {
    // Copy planned to actual only if actual is empty or 0
    setFinancialOverview(prev => {
      const plannedValue = prev[item].planned;
      const actualValue = prev[item].actual;
      
      if (plannedValue && (!actualValue || actualValue === '0' || actualValue === 0)) {
        return {
          ...prev,
          [item]: {
            ...prev[item],
            actual: plannedValue
          }
        };
      }
      return prev;
    });
  }, []);

  const calculateTotal = (data, field) => {
    return data.reduce((sum, item) => sum + (parseFloat(item[field]) || 0), 0);
  };

  // API Callback Functions
  const handleAddItem = useCallback(async (type, itemId, name, amount) => {
    if (!budgetId) return;
    
    try {
      await BudgetService.createBudgetItem(budgetId, name, amount, type, itemId);
    } catch (error) {
      console.error('Error adding budget item:', error);
    }
  }, [budgetId]);

  const handleUpdateItem = useCallback(async (itemId, name, amount) => {
    if (!budgetId) return;
    
    try {
      await BudgetService.updateBudgetItem(budgetId, itemId, name, amount);
    } catch (error) {
      console.error('Error updating budget item:', error);
    }
  }, [budgetId]);

  const handleDeleteItem = useCallback(async (itemId) => {
    if (!budgetId) return;
    
    try {
      await BudgetService.deleteBudgetItem(budgetId, itemId);
    } catch (error) {
      console.error('Error deleting budget item:', error);
    }
  }, [budgetId]);

  // Load budget data on mount and when month changes
  useEffect(() => {
    const loadBudgetData = async () => {
      try {
        setLoading(true);
        const userId = localStorage.getItem('userId');
        if (!userId) {
          console.error('User ID not found');
          setLoading(false);
          return;
        }

        // Format yearMonth as YYYY-MM
        const yearMonth = format(selectedDate, 'yyyy-MM');

        // Get currency code from current currency symbol
        const currencyCode = currencySymbolToCode[currency] || 'USD';

        // Create or get budget for this month
        const budgetResponse = await BudgetService.createOrGetBudget(
          userId, 
          yearMonth, 
          currencyCode,
          budgetPeriod.start || '',
          budgetPeriod.end || ''
        );
        setBudgetId(budgetResponse.id);

        // Set currency from response if available
        if (budgetResponse.currencyCode) {
          const currencySymbol = currencyCodeToSymbol[budgetResponse.currencyCode] || '$';
          setCurrency(currencySymbol);
        }

        // Set budget period from response if available
        if (budgetResponse.startDate || budgetResponse.endDate) {
          setBudgetPeriod({
            start: budgetResponse.startDate || null,
            end: budgetResponse.endDate || null,
          });
        }

        // Load overview data from budget response
        const overview = budgetResponse.overview || {};
        setFinancialOverview({
          rollover: { 
            planned: overview.rollover?.toString() || '0', 
            actual: overview.actualRollover?.toString() || '0' 
          },
          income: { 
            planned: overview.income?.toString() || '0', 
            actual: '0' // Will be calculated from items
          },
          expenses: { 
            planned: overview.expenses?.toString() || '0', 
            actual: '0' // Will be calculated from items
          },
          bills: { 
            planned: overview.bills?.toString() || '0', 
            actual: '0' // Will be calculated from items
          },
          savings: { 
            planned: overview.savings?.toString() || '0', 
            actual: '0' // Will be calculated from items
          },
          investing: { 
            planned: overview.investing?.toString() || '0', 
            actual: '0' // Will be calculated from items
          },
          debt: { 
            planned: overview.debts?.toString() || '0', 
            actual: '0' // Will be calculated from items
          },
        });

        // Get all budget items
        const itemsResponse = await BudgetService.getBudgetItems(budgetResponse.id);
        
        // Map items by type
        const itemsByType = {
          INCOME: [],
          EXPENSES: [],
          BILLS: [],
          SAVINGS: [],
          INVESTING: [],
          DEBT: []
        };

        itemsResponse.budgetItems.forEach(item => {
          const mappedItem = {
            id: item.id,
            name: item.name,
            amount: item.amount.toString(),
            createdOn: item.createdOn
          };
          
          if (itemsByType[item.type]) {
            itemsByType[item.type].push(mappedItem);
          }
        });

        // Sort items by createdOn for each category
        Object.keys(itemsByType).forEach(type => {
          itemsByType[type].sort((a, b) => new Date(a.createdOn) - new Date(b.createdOn));
        });

        // Update state with fetched data
        setIncomeData(itemsByType.INCOME);
        setExpensesData(itemsByType.EXPENSES);
        setBillsData(itemsByType.BILLS);
        setSavingsData(itemsByType.SAVINGS);
        setInvestingData(itemsByType.INVESTING);
        setDebtData(itemsByType.DEBT);

        setLoading(false);
      } catch (error) {
        console.error('Error loading budget data:', error);
        setLoading(false);
      }
    };

    loadBudgetData();
  }, [selectedDate]);

  // Sync Income table totals to Financial Overview (only actual, planned is manual)
  useEffect(() => {
    const total = calculateTotal(incomeData, "amount");
    
    setFinancialOverview(prev => ({
      ...prev,
      income: {
        ...prev.income,
        actual: total.toString(),
      }
    }));
  }, [incomeData]);

  // Sync Expenses table totals to Financial Overview (only actual, planned is manual)
  useEffect(() => {
    const total = calculateTotal(expensesData, "amount");
    
    setFinancialOverview(prev => ({
      ...prev,
      expenses: {
        ...prev.expenses,
        actual: total.toString(),
      }
    }));
  }, [expensesData]);

  // Sync Bills table totals to Financial Overview (only actual, planned is manual)
  useEffect(() => {
    const total = calculateTotal(billsData, "amount");
    
    setFinancialOverview(prev => ({
      ...prev,
      bills: {
        ...prev.bills,
        actual: total.toString(),
      }
    }));
  }, [billsData]);

  // Sync Savings table totals to Financial Overview (only actual, planned is manual)
  useEffect(() => {
    const total = calculateTotal(savingsData, "amount");
    
    setFinancialOverview(prev => ({
      ...prev,
      savings: {
        ...prev.savings,
        actual: total.toString(),
      }
    }));
  }, [savingsData]);

  // Sync Debt table totals to Financial Overview (only actual, planned is manual)
  useEffect(() => {
    const total = calculateTotal(debtData, "amount");
    
    setFinancialOverview(prev => ({
      ...prev,
      debt: {
        ...prev.debt,
        actual: total.toString(),
      }
    }));
  }, [debtData]);

  // Sync Investing table totals to Financial Overview (only actual, planned is manual)
  useEffect(() => {
    const total = calculateTotal(investingData, "amount");
    
    setFinancialOverview(prev => ({
      ...prev,
      investing: {
        ...prev.investing,
        actual: total.toString(),
      }
    }));
  }, [investingData]);

  // Calculate Amount Left to Spend from Financial Overview (matches the Left row)
  const rolloverActual = parseFloat(financialOverview.rollover.actual || 0);
  const incomeActual = parseFloat(financialOverview.income.actual || 0);
  const expensesActual = parseFloat(financialOverview.expenses.actual || 0);
  const billsActual = parseFloat(financialOverview.bills.actual || 0);
  const savingsActual = parseFloat(financialOverview.savings.actual || 0);
  const investingActual = parseFloat(financialOverview.investing.actual || 0);
  const debtActual = parseFloat(financialOverview.debt.actual || 0);
  
  const amountLeftToSpend = rolloverActual + incomeActual - expensesActual - billsActual - savingsActual - investingActual - debtActual;

  const handlePreviousMonth = () => {
    setIsFlipping(true);
    setTimeout(() => {
      setSelectedDate(subMonths(selectedDate, 1));
      setIsFlipping(false);
    }, 300);
  };

  const handleNextMonth = () => {
    setIsFlipping(true);
    setTimeout(() => {
      setSelectedDate(addMonths(selectedDate, 1));
      setIsFlipping(false);
    }, 300);
  };

  const handleMonthChange = (event) => {
    setIsFlipping(true);
    setTimeout(() => {
      const newDate = new Date(selectedDate);
      newDate.setMonth(event.target.value);
      setSelectedDate(newDate);
      setIsFlipping(false);
    }, 300);
  };

  const handleCurrencyChange = async (event) => {
    const newCurrency = event.target.value;
    setCurrency(newCurrency);
    
    // Update budget on backend if budgetId is available
    if (budgetId) {
      try {
        const userId = localStorage.getItem('userId');
        if (userId) {
          const currencyCode = currencySymbolToCode[newCurrency] || 'USD';
          await BudgetService.updateBudget(
            userId, 
            budgetId, 
            currencyCode, 
            budgetPeriod.start || '', 
            budgetPeriod.end || ''
          );
        }
      } catch (error) {
        console.error('Error updating budget:', error);
      }
    }
  };

  const handleBudgetPeriodChange = async (field, value) => {
    setBudgetPeriod(prev => ({ ...prev, [field]: value }));
    
    // Update budget on backend if budgetId is available
    if (budgetId) {
      try {
        const userId = localStorage.getItem('userId');
        if (userId) {
          const currencyCode = currencySymbolToCode[currency] || 'USD';
          const updatedPeriod = { ...budgetPeriod, [field]: value };
          await BudgetService.updateBudget(
            userId, 
            budgetId, 
            currencyCode, 
            updatedPeriod.start || '', 
            updatedPeriod.end || ''
          );
        }
      } catch (error) {
        console.error('Error updating budget:', error);
      }
    }
  };

  const flipAnimation = {
    transform: isFlipping ? 'rotateX(90deg)' : 'rotateX(0deg)',
    transition: 'transform 0.3s ease-in-out',
    transformStyle: 'preserve-3d',
    perspective: '1000px',
  };

  // Allocation Summary Chart Data - use Financial Overview actual data
  const allocationData = useMemo(() => ({
    labels: ["Expenses", "Bills", "Savings", "Investing", "Debt"],
    datasets: [
      {
        data: [
          parseFloat(financialOverview.expenses.actual || 0),
          parseFloat(financialOverview.bills.actual || 0),
          parseFloat(financialOverview.savings.actual || 0),
          parseFloat(financialOverview.investing.actual || 0),
          parseFloat(financialOverview.debt.actual || 0),
        ],
        backgroundColor: ["#7c88a2", "#8a94b0", "#98a0be", "#a6accb", "#b4b8d9"],
        borderWidth: 0,
      },
    ],
  }), [financialOverview]);

  // Cash Flow Chart Data - use Financial Overview data for better performance
  const cashFlowData = useMemo(() => ({
    labels: ["Rollover", "Income", "Expenses", "Bills", "Savings", "Investing", "Debt"],
    datasets: [
      {
        label: "Planned",
        data: [
          parseFloat(financialOverview.rollover.planned || 0),
          parseFloat(financialOverview.income.planned || 0),
          parseFloat(financialOverview.expenses.planned || 0),
          parseFloat(financialOverview.bills.planned || 0),
          parseFloat(financialOverview.savings.planned || 0),
          parseFloat(financialOverview.investing.planned || 0),
          parseFloat(financialOverview.debt.planned || 0),
        ],
        backgroundColor: "#7c88a2",
      },
      {
        label: "Actual",
        data: [
          parseFloat(financialOverview.rollover.actual || 0),
          parseFloat(financialOverview.income.actual || 0),
          parseFloat(financialOverview.expenses.actual || 0),
          parseFloat(financialOverview.bills.actual || 0),
          parseFloat(financialOverview.savings.actual || 0),
          parseFloat(financialOverview.investing.actual || 0),
          parseFloat(financialOverview.debt.actual || 0),
        ],
        backgroundColor: "#8a94b0",
      },
    ],
  }), [financialOverview]);

  const formatCurrency = (value) => {
    return `${currency}${parseFloat(value || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  return (
    <DashboardLayout>
      <MDBox 
        sx={{ 
          backgroundColor: "#F8F9FA", 
          minHeight: "100vh", 
          p: 3,
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start"
        }}
      >
      <Box sx={{ maxWidth: "1400px", width: "100%" }}>
      {/* Header Section with Month Picker */}
      <Box sx={{ mb: 4, display: "flex", flexDirection: "column", alignItems: "center" }}>
        <Typography variant="h3" sx={{ fontWeight: 300, mb: 3 }}>
          {format(selectedDate, "MMMM yyyy")}
        </Typography>
        
        {/* Month Picker */}
        <Box 
          display="flex" 
          alignItems="center" 
          gap={2}
          sx={{
            backgroundColor: "white",
            borderRadius: 2,
            p: 1.5,
            maxWidth: 400,
            border: "1px solid #E0E0E0",
          }}
        >
          <IconButton onClick={handlePreviousMonth} size="small">
            <ArrowBackIosNewIcon />
          </IconButton>
          <Select
            value={selectedDate.getMonth()}
            onChange={handleMonthChange}
            variant="standard"
            disableUnderline
            sx={{
              flex: 1,
              textAlign: 'center',
              '& .MuiSelect-select': {
                py: 1,
                fontSize: '1rem',
                fontWeight: 500,
              },
            }}
          >
            {months.map((month, index) => (
              <MenuItem key={month} value={index}>
                {month} {selectedDate.getFullYear()}
              </MenuItem>
            ))}
          </Select>
          <IconButton onClick={handleNextMonth} size="small">
            <ArrowForwardIosIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Top Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3, ...flipAnimation }}>
        <Grid item xs={12} md={2}>
          <Card sx={{ p: 2, textAlign: "center", backgroundColor: "#7c88a2", color: "white" }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              INCOME
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              {formatCurrency(incomeActual)}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={2}>
          <Card sx={{ p: 2, textAlign: "center", backgroundColor: "#7c88a2", color: "white" }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              EXPENSES
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              {formatCurrency(expensesActual)}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={2}>
          <Card sx={{ p: 2, textAlign: "center", backgroundColor: "#7c88a2", color: "white", border: "2px solid #3a4560" }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              BILLS
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              {formatCurrency(billsActual)}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={2}>
          <Card sx={{ p: 2, textAlign: "center", backgroundColor: "#7c88a2", color: "white" }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              SAVINGS
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              {formatCurrency(savingsActual)}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={2}>
          <Card sx={{ p: 2, textAlign: "center", backgroundColor: "#7c88a2", color: "white" }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              INVESTING
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              {formatCurrency(investingActual)}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={2}>
          <Card sx={{ p: 2, textAlign: "center", backgroundColor: "#7c88a2", color: "white" }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              DEBT
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              {formatCurrency(debtActual)}
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Budget Period and Settings */}
      <Grid container spacing={2} sx={{ mb: 3, ...flipAnimation }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
              BUDGET PERIOD
            </Typography>
            <Box display="flex" gap={2} alignItems="center" sx={{ flex: 1 }}>
              <TextField
                type="date"
                size="small"
                value={budgetPeriod.start || ''}
                onChange={(e) => handleBudgetPeriodChange('start', e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                placeholder="Start Date"
              />
              <Typography>-</Typography>
              <TextField
                type="date"
                size="small"
                value={budgetPeriod.end || ''}
                onChange={(e) => handleBudgetPeriodChange('end', e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                placeholder="End Date"
              />
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2, display: 'flex', flexDirection: 'column', height: "100%" }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
              CURRENCY
            </Typography>
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
              <Select
                size="small"
                value={currency}
                onChange={handleCurrencyChange}
                fullWidth
              >
                <MenuItem value="$">$ (USD)</MenuItem>
                <MenuItem value="€">€ (EUR)</MenuItem>
                <MenuItem value="£">£ (GBP)</MenuItem>
              </Select>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={2} sx={{ mb: 3, ...flipAnimation }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 2, height: "100%" }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: "bold", textAlign: "center" }}>
              AMOUNT LEFT TO SPEND
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: 200,
                position: "relative",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                  {formatCurrency(amountLeftToSpend)}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 2, height: "100%" }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: "bold", textAlign: "center" }}>
              CASH FLOW
            </Typography>
            <Box sx={{ height: 200 }}>
              <Bar
                data={cashFlowData}
                options={{
                  indexAxis: "y",
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { position: "bottom" } },
                  scales: {
                    x: { beginAtZero: true },
                  },
                }}
              />
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 2, height: "100%" }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: "bold", textAlign: "center" }}>
              ALLOCATION SUMMARY
            </Typography>
            <Box sx={{ height: 200, display: "flex", justifyContent: "center" }}>
              <Doughnut
                data={allocationData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { position: "right", labels: { boxWidth: 15, fontSize: 10 } },
                  },
                }}
              />
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Financial Overview Table */}
      <Box sx={{ mb: 3, ...flipAnimation }}>
        <Typography
          variant="h6"
          sx={{
            backgroundColor: "#7c88a2",
            color: "white",
            p: 1.5,
            textAlign: 'center',
            fontWeight: 'bold',
            borderTopLeftRadius: 1,
            borderTopRightRadius: 1,
          }}
        >
          FINANCIAL OVERVIEW
        </Typography>
        <Box
          sx={{
            border: `1px solid #d0d0d0`,
            borderRadius: 0,
          }}
        >
          {/* Header Row */}
          <Grid container sx={{ backgroundColor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
            <Grid item xs={2.4} sx={{ padding: '8px', borderRight: '1px solid #e0e0e0' }}>
            </Grid>
            <Grid item xs={4.8} sx={{ padding: '8px', borderRight: '1px solid #e0e0e0', textAlign: 'center' }}>
              <Typography sx={{ fontWeight: 'bold', textTransform: 'uppercase', fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
                PLANNED
              </Typography>
            </Grid>
            <Grid item xs={4.8} sx={{ padding: '8px', textAlign: 'center' }}>
              <Typography sx={{ fontWeight: 'bold', textTransform: 'uppercase', fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
                ACTUAL
              </Typography>
            </Grid>
          </Grid>
          
          {/* Data Rows */}
          {["Rollover", "Income", "Expenses", "Bills", "Savings", "Investing", "Debt", "Left"].map((item, index) => {
            const itemKey = item.toLowerCase();
            const isLeftRow = item === "Left";
            
            // Calculate Left row values
            let plannedValue = 0;
            let actualValue = 0;
            
            if (isLeftRow) {
              plannedValue = parseFloat(financialOverview.rollover.planned || 0) +
                            parseFloat(financialOverview.income.planned || 0) -
                            (parseFloat(financialOverview.expenses.planned || 0) +
                             parseFloat(financialOverview.bills.planned || 0) +
                             parseFloat(financialOverview.savings.planned || 0) +
                             parseFloat(financialOverview.investing.planned || 0) +
                             parseFloat(financialOverview.debt.planned || 0));
              actualValue = parseFloat(financialOverview.rollover.actual || 0) +
                           parseFloat(financialOverview.income.actual || 0) -
                           (parseFloat(financialOverview.expenses.actual || 0) +
                            parseFloat(financialOverview.bills.actual || 0) +
                            parseFloat(financialOverview.savings.actual || 0) +
                            parseFloat(financialOverview.investing.actual || 0) +
                            parseFloat(financialOverview.debt.actual || 0));
            } else {
              plannedValue = financialOverview[itemKey]?.planned || '';
              actualValue = financialOverview[itemKey]?.actual || '';
            }

            return (
              <Grid 
                container 
                key={item}
                sx={{
                  '&:hover': { backgroundColor: '#f5f5f5' },
                  borderBottom: '1px solid #e0e0e0',
                  backgroundColor: isLeftRow ? '#f5f5f5' : 'white',
                  ...(isLeftRow && { borderTop: '2px solid #d0d0d0' }),
                }}
              >
                <Grid item xs={2.4} sx={{ padding: '8px', borderRight: '1px solid #e0e0e0' }}>
                  <Typography sx={{ fontWeight: isLeftRow ? 'bold' : 'normal', fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
                    {item}
                  </Typography>
                </Grid>
                <Grid item xs={4.8} sx={{ padding: '8px', borderRight: '1px solid #e0e0e0', backgroundColor: isLeftRow ? '#f5f5f5' : 'white' }}>
                  {isLeftRow ? (
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: "bold",
                        textAlign: "center",
                        color: plannedValue < 0 ? "error.main" : "inherit",
                        fontSize: { xs: '0.75rem', md: '0.875rem' }
                      }}
                    >
                      {formatCurrency(plannedValue)}
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      <Typography
                        component="span"
                        sx={{
                          fontSize: { xs: '0.75rem', md: '0.875rem' },
                          color: '#666',
                        }}
                      >
                        {currency}
                      </Typography>
                      <TextField
                        type="text"
                        size="small"
                        value={plannedValue}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                            handleFinancialOverviewChange(itemKey, 'planned', value);
                          }
                        }}
                        onBlur={() => handlePlannedBlur(itemKey)}
                        placeholder=""
                        sx={{
                          width: { xs: '80px', md: '120px' },
                          '& .MuiInputBase-input': {
                            textAlign: 'right',
                            py: 0.5,
                            px: 1,
                            fontSize: { xs: '0.75rem', md: '0.875rem' },
                            cursor: 'text',
                            backgroundColor: 'white',
                          },
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { 
                              border: 'none',
                            },
                            '&:hover': {
                              backgroundColor: '#f0f8ff',
                            },
                            '&.Mui-focused': {
                              backgroundColor: 'white',
                              boxShadow: 'inset 0 0 0 2px #4285f4',
                            },
                          },
                        }}
                      />
                    </Box>
                  )}
                </Grid>
                <Grid item xs={4.8} sx={{ padding: '8px', backgroundColor: isLeftRow ? '#f5f5f5' : 'white' }}>
                  {isLeftRow ? (
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: "bold",
                        textAlign: "center",
                        color: actualValue < 0 ? "error.main" : "inherit",
                        fontSize: { xs: '0.75rem', md: '0.875rem' }
                      }}
                    >
                      {formatCurrency(actualValue)}
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      <Typography
                        component="span"
                        sx={{
                          fontSize: { xs: '0.75rem', md: '0.875rem' },
                          color: '#666',
                        }}
                      >
                        {currency}
                      </Typography>
                      <TextField
                        type="text"
                        size="small"
                        value={actualValue}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                            handleFinancialOverviewChange(itemKey, 'actual', value);
                          }
                        }}
                        placeholder=""
                        sx={{
                          width: { xs: '80px', md: '120px' },
                          '& .MuiInputBase-input': {
                            textAlign: 'right',
                            py: 0.5,
                            px: 1,
                            fontSize: { xs: '0.75rem', md: '0.875rem' },
                            cursor: 'text',
                            backgroundColor: 'white',
                          },
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { 
                              border: 'none',
                            },
                            '&:hover': {
                              backgroundColor: '#f0f8ff',
                            },
                            '&.Mui-focused': {
                              backgroundColor: 'white',
                              boxShadow: 'inset 0 0 0 2px #4285f4',
                            },
                          },
                        }}
                      />
                    </Box>
                  )}
                </Grid>
              </Grid>
            );
          })}
        </Box>
      </Box>

      {/* Dynamic Tables Section */}
      <Grid container spacing={2} sx={flipAnimation}>
        <Grid item xs={12} md={4}>
          <DynamicTable
            title="INCOME"
            columns={incomeColumns}
            data={incomeData}
            onChange={setIncomeData}
            headerColor="#7c88a2"
            currency={currency}
            onAddItem={(itemId, name, amount) => handleAddItem('INCOME', itemId, name, amount)}
            onUpdateItem={handleUpdateItem}
            onDeleteItem={handleDeleteItem}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <DynamicTable
            title="EXPENSES"
            columns={expensesColumns}
            data={expensesData}
            onChange={setExpensesData}
            headerColor="#7c88a2"
            currency={currency}
            onAddItem={(itemId, name, amount) => handleAddItem('EXPENSES', itemId, name, amount)}
            onUpdateItem={handleUpdateItem}
            onDeleteItem={handleDeleteItem}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <DynamicTable
            title="BILLS"
            columns={billsColumns}
            data={billsData}
            onChange={setBillsData}
            headerColor="#7c88a2"
            currency={currency}
            onAddItem={(itemId, name, amount) => handleAddItem('BILLS', itemId, name, amount)}
            onUpdateItem={handleUpdateItem}
            onDeleteItem={handleDeleteItem}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <DynamicTable
            title="SAVINGS"
            columns={savingsColumns}
            data={savingsData}
            onChange={setSavingsData}
            headerColor="#7c88a2"
            currency={currency}
            onAddItem={(itemId, name, amount) => handleAddItem('SAVINGS', itemId, name, amount)}
            onUpdateItem={handleUpdateItem}
            onDeleteItem={handleDeleteItem}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <DynamicTable
            title="INVESTING"
            columns={investingColumns}
            data={investingData}
            onChange={setInvestingData}
            headerColor="#7c88a2"
            currency={currency}
            onAddItem={(itemId, name, amount) => handleAddItem('INVESTING', itemId, name, amount)}
            onUpdateItem={handleUpdateItem}
            onDeleteItem={handleDeleteItem}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <DynamicTable
            title="DEBT"
            columns={debtColumns}
            data={debtData}
            onChange={setDebtData}
            headerColor="#7c88a2"
            currency={currency}
            onAddItem={(itemId, name, amount) => handleAddItem('DEBT', itemId, name, amount)}
            onUpdateItem={handleUpdateItem}
            onDeleteItem={handleDeleteItem}
          />
        </Grid>
      </Grid>
      </Box>
    </MDBox>
    </DashboardLayout>
  );
};

export default BudgetDashboard;
